import { Smartphone } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { appStoreListings, type AppStoreListing } from '../../lib/appStores'
import { cn } from '../../lib/cn'

/*
 * ⚠️ ONE NEUTRAL GLYPH FOR BOTH, AND IT IS NOT AN OVERSIGHT. This used lucide's
 * `Apple` and `Play`. lucide's `Apple` is a piece of FRUIT, not Apple Inc.'s
 * mark, and `Play` is a triangle that reads as Google Play's. Neither company
 * permits a redrawn mark; both publish official badges with fixed artwork and
 * wording. The mobile app removed the same glyph from its sign-in buttons on
 * 12 Oct for the same reason — a wrong mark looks finished and fails review
 * quietly. QA 19 Oct (WEB-009). On launch day the whole chip becomes the
 * official badge artwork, together with the hrefs in `lib/appStores.ts`.
 */
const STORE_ICON: Record<AppStoreListing['id'], LucideIcon> = {
  ios: Smartphone,
  android: Smartphone,
}

const STORE_EYEBROW: Record<AppStoreListing['id'], 'app.badges.downloadOn' | 'app.badges.getItOn'> =
  {
    ios: 'app.badges.downloadOn',
    android: 'app.badges.getItOn',
  }

type AppStoreBadgesProps = {
  className?: string
  /** Inverts the badge for use on the deep-blue CTA band. */
  tone?: 'default' | 'onPrimary'
}

/**
 * The store badges, which appear on the hero, both audience pages, the CTA band
 * and `/download`.
 *
 * ⚠️ THEY ARE NOT LINKS, BECAUSE THERE IS NOTHING TO LINK TO.
 * `lib/appStores.ts` has the full reasoning and is the one place to change when
 * the listings exist. In short: the previous URLs were placeholders, and five
 * pages rendered them as working anchors — a tap sent the visitor to a store
 * 404 with no explanation. A badge that says "coming soon" is worse marketing
 * and better honesty, and it is the same judgement the rest of this homepage
 * now makes about the platform's numbers.
 *
 * The chip keeps the anchor's box and height exactly, so the layout does not
 * move the day the URLs land — only the element and the eyebrow change.
 */
export const AppStoreBadges = ({ className, tone = 'default' }: AppStoreBadgesProps) => {
  const { t } = useTranslation()

  return (
    <ul className={cn('flex flex-wrap items-center gap-3', className)}>
      {appStoreListings.map((store) => {
        const Icon = STORE_ICON[store.id]

        const box = cn(
          // `touch-target` is kept on the inert chip too: it is a sizing
          // utility here, and matching the anchor's height is what keeps the
          // hero from reflowing when these become links.
          'touch-target inline-flex items-center gap-3 rounded-lg border px-4 py-2',
          'transition-colors duration-hover ease-out',
        )

        const body = (
          <>
            <Icon aria-hidden="true" className="size-6 shrink-0" />
            <span className="flex flex-col text-left leading-tight">
              <span className="type-caption opacity-80">
                {t(store.href === null ? 'app.badges.comingSoonTo' : STORE_EYEBROW[store.id])}
              </span>
              <span className="type-body font-semibold">{store.name}</span>
            </span>
          </>
        )

        return (
          <li key={store.id}>
            {store.href === null ? (
              <span
                className={cn(
                  box,
                  // Dashed and muted: the border itself says "not finished",
                  // before the words are read. `current` on the CTA band
                  // because that slab's foreground differs from the page's.
                  tone === 'onPrimary'
                    ? 'border-current/25 border-dashed text-current opacity-75'
                    : 'border-dashed border-border bg-muted/40 text-muted-foreground',
                )}
              >
                {body}
              </span>
            ) : (
              <a
                href={store.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t('app.badges.ariaLabel', { store: store.name })}
                className={cn(
                  box,
                  // `current` rather than a named token: the badge sits on a band
                  // whose colour differs between light and dark, and inheriting the
                  // band's own foreground keeps the contrast correct in both.
                  tone === 'onPrimary'
                    ? 'border-current/25 bg-current/10 text-current hover:bg-current/20'
                    : 'border-border bg-card text-card-foreground hover:border-primary/40 hover:bg-muted',
                )}
              >
                {body}
              </a>
            )}
          </li>
        )
      })}
    </ul>
  )
}
